/** 集装箱 Mock数据 */

import type { Container, NodeRecord, ContainerArchiveLog, ContainerAlertSummary, ContainerSubstep, SubstepChecklistItem } from '../types';
import { NODE_CODES } from '../types/node-substep';
import { ContainerType, ContainerStatus, NodeStatus } from '../types';
import { randomPick, randomTimestamp, randomInt } from '../utils/mockHelper';
import { NODE_NAMES, NODE_SHORT_NAMES, GOODS_PREP_CHECKLIST } from '../utils/constants';
import { mockOrders } from './orders';

const vessels = [
  { name: 'COSCO SHIPPING LEO', voyage: 'V087E' },
  { name: 'MAERSK MCKINNEY', voyage: 'M2512' },
  { name: 'MSC DIANA', voyage: 'FD841W' },
  { name: 'CMA CGM JACQUES', voyage: '0PG5WE' },
  { name: 'ONE APUS', voyage: '073E' },
  { name: 'EVER GOLDEN', voyage: 'EG135N' },
  { name: 'HMM ALGECIRAS', voyage: 'H045E' },
  { name: 'YANG MING WISH', voyage: 'YM109S' },
];

const ports = [
  { lat: 31.2304, lng: 121.4737, portName: '上海港', country: '中国' },
  { lat: 22.5431, lng: 114.0579, portName: '深圳港', country: '中国' },
  { lat: 29.8683, lng: 121.5440, portName: '宁波港', country: '中国' },
  { lat: 36.0671, lng: 120.3826, portName: '青岛港', country: '中国' },
  { lat: 33.7490, lng: -118.2200, portName: '洛杉矶港', country: '美国' },
  { lat: 51.9076, lng: 4.4792, portName: '鹿特丹港', country: '荷兰' },
  { lat: 53.5488, lng: 9.9872, portName: '汉堡港', country: '德国' },
  { lat: 1.2641, lng: 103.8400, portName: '新加坡港', country: '新加坡' },
];

const operators = [
  '张伟', '李强', '王芳', '赵敏', '刘洋',
  '陈静', '杨帆', '周涛', '吴磊', '郑洁',
];

function generateNodeHistory(containerIndex: number, currentNode: number): NodeRecord[] {
  // alert 节点配置：特定订单触发预警
  const alertNodeId =
    containerIndex === 2 ? 5 :
    containerIndex === 6 ? 6 :
    null;

  return NODE_NAMES.map((name, idx) => {
    const nodeId = idx + 1;
    let status: NodeStatus;
    if (nodeId < currentNode) status = NodeStatus.COMPLETED;
    else if (nodeId === currentNode) status = NodeStatus.IN_PROGRESS;
    else status = NodeStatus.PENDING;

    // alert 节点覆盖
    if (alertNodeId === nodeId) status = NodeStatus.ALERT;

    // 关键修复：alert 节点之前的节点不能是 pending/current（必须都是 done）
    // 即：走到 export_customs(5) 才出 alert，那 order(1)-export_customs(4) 都应已完成
    if (alertNodeId && nodeId < alertNodeId) {
      status = NodeStatus.COMPLETED;
    }

    return {
      nodeId,
      nodeName: name,
      status,
      operatorId: `OP${String((idx % operators.length) + 1).padStart(3, '0')}`,
      operatorName: status !== NodeStatus.PENDING ? operators[idx % operators.length] : '',
      timestamp: status !== NodeStatus.PENDING ? randomTimestamp(30) : '',
      photos: status === NodeStatus.COMPLETED ? [`/photos/cont${containerIndex}_node${nodeId}.jpg`] : [],
      notes: status === NodeStatus.COMPLETED ? '检验合格，无异常' : '',
    };
  });
}

/** 计算停留时长的格式化字符串 */
function computeRemainingStay(currentNode: number, containerIndex: number): string {
  // 基于集装箱索引和当前节点计算一个合理的停留时长
  const days = (containerIndex % 3) + (currentNode % 5);
  const hours = (containerIndex * 7 + currentNode * 3) % 24;
  if (days === 0) return `${hours}小时`;
  return `${days}天${hours}小时`;
}

/** 从 container.nodeHistory 生成时间线日志（固定11条） */
export function generateArchiveLogs(container: Container): ContainerArchiveLog[] {
  const logs: ContainerArchiveLog[] = [];

  // 取 nodeHistory 中非 PENDING 的节点（最多10条）
  const activeNodes = container.nodeHistory.filter(
    (node: NodeRecord) => node.status !== NodeStatus.PENDING,
  );

  for (let i = 0; i < activeNodes.length; i++) {
    const node = activeNodes[i];
    const nodeIdx = node.nodeId - 1;
    const shortName = NODE_SHORT_NAMES[nodeIdx] ?? `N${node.nodeId}`;
    let logStatus: ContainerArchiveLog['logStatus'] = 'normal';

    if (node.status === NodeStatus.ALERT) {
      logStatus = 'danger';
    } else if (node.status === NodeStatus.IN_PROGRESS) {
      // IN_PROGRESS节点：根据温度判断是否warning
      logStatus = container.temperature > 6 ? 'warning' : 'normal';
    }

    logs.push({
      id: `log-${container.id}-${node.nodeId}`,
      containerId: container.id,
      nodeCode: NODE_CODES[node.nodeId - 1],
      timestamp: node.timestamp || new Date().toISOString(),
      operatorName: node.operatorName,
      nodeName: shortName,
      description: node.notes || `${node.nodeName} — 操作完成`,
      logStatus,
    });
  }

  // IoT设备仅在装柜节点（nodeId=4）完成后才绑定，未绑定时不生成IoT监控日志
  const isIoTBound =
    container.nodeHistory.find((n) => n.nodeId === 4)?.status === NodeStatus.COMPLETED;

  // 如果不足11条，追加温度告警记录补齐
  while (logs.length < 11) {
    if (!isIoTBound) break;
    const extraIdx = logs.length;
    logs.push({
      id: `log-${container.id}-extra-${extraIdx}`,
      containerId: container.id,
      nodeCode: NODE_CODES[container.currentNode - 1],
      timestamp: randomTimestamp(15 + extraIdx),
      operatorName: '系统',
      nodeName: 'IoT监控',
      description: `温度监测: ${container.temperature}°C, 湿度: ${container.humidity}% — 数据正常`,
      logStatus: 'normal',
    });
  }

  // 如果超过11条，截断到11条
  return logs.slice(0, 11);
}

/** 生成预警汇总数据 */
export function deriveAlertSummary(containers: Container[]): ContainerAlertSummary {
  const alertContainers = containers.filter((c: Container) => {
    // 检查是否有 ALERT 节点
    const hasAlertNode = c.nodeHistory.some((n: NodeRecord) => n.status === NodeStatus.ALERT);
    // 检查温度是否异常
    const tempAbnormal = c.temperature > 6;
    // 检查停留时长
    const stayDays = parseInt(c.remainingStay, 10) || 0;
    const stayAbnormal = stayDays > 2;
    return hasAlertNode || tempAbnormal || stayAbnormal;
  });

  const items = alertContainers.map((c: Container) => ({
    containerNo: c.containerNo,
    archiveNo: c.archiveNo,
    alertReason: c.nodeHistory.some((n: NodeRecord) => n.status === NodeStatus.ALERT)
      ? `${c.nodeHistory.find((n: NodeRecord) => n.status === NodeStatus.ALERT)?.nodeName ?? '未知节点'} 发生异常`
      : c.temperature > 6
        ? `温度异常 ${c.temperature}°C`
        : `节点停留超时 ${c.remainingStay}`,
  }));

  return {
    count: alertContainers.length,
    items,
  };
}

/** 手工填充：装柜节点（原提空柜，已归入N4装柜）的测试数据 */
const testAtPickup: Container = {
  id: 'CONT_TEST',
  containerNo: '',
  orderId: 'BLU-2607-0099',
  type: ContainerType.REEFER_40,
  sealNo: '',
  currentNode: 4,
  totalNodes: 10,
  nodeHistory: NODE_NAMES.map((name, idx) => {
    const nodeId = idx + 1;
    let status: NodeStatus;
    // 10节点模型：N4=装柜(含订舱/提空柜)，走到N4表示刚提柜待装
    if (nodeId < 4) status = NodeStatus.COMPLETED;
    else if (nodeId === 4) status = NodeStatus.IN_PROGRESS;
    else status = NodeStatus.PENDING;
    return {
      nodeId,
      nodeName: name,
      status,
      operatorId: `OP${String((idx % 10) + 1).padStart(3, '0')}`,
      operatorName: status !== NodeStatus.PENDING ? operators[idx % operators.length] : '',
      timestamp: status !== NodeStatus.PENDING ? randomTimestamp(30) : '',
      photos: [],
      notes: status === NodeStatus.COMPLETED ? '检验合格，无异常' : '',
    };
  }),
  iotDeviceId: '',
  temperature: 0,
  humidity: 0,
  location: { lat: 0, lng: 0, portName: '', country: '' },
  status: ContainerStatus.AT_ORIGIN,
  vesselName: 'COSCO SHIPPING TAURUS',
  voyageNo: 'V.2607E',
  estimatedArrival: new Date(Date.now() + 15 * 86400000).toISOString(),
  actualArrival: '',
  archiveNo: '',
  category: '大蒜',
  route: '上海 → 洛杉矶',
  remainingStay: '2天5小时',
  // 演示用：N4 · 柜号空 · 4.1 已完成 → 触发「新建柜档案」入口
  bookingNo: 'SO-BLU-2607-0099',
  currentSubstep: 'loading',
};

/** 从订单派生柜档案 */
function deriveContainerFromOrder(order: import('../types').Order, index: number): Container {
  const vessel = randomPick(vessels);
  const typeValues = [ContainerType.REEFER_20, ContainerType.REEFER_40, ContainerType.DRY_20, ContainerType.DRY_40];
  const currentNode = order.currentNodeNumber;
  const nodeHistory = generateNodeHistory(index, currentNode);
  const containerNo = currentNode >= 4 ? order.containerMapping.containerNos[0] || `BLU-C-${2600 + index}` : '';
  const archiveNo = currentNode >= 4 ? `DA-2026-${String(index + 1).padStart(4, '0')}` : '';

  return {
    id: `CONT${String(index + 1).padStart(3, '0')}`,
    containerNo,
    orderId: order.orderNo,
    type: typeValues[index % 4],
    sealNo: currentNode >= 4 ? `SEAL${10000 + index}` : '',
    currentNode,
    totalNodes: 10,
    nodeHistory,
    iotDeviceId: currentNode >= 4 ? `DEV${String(index + 1).padStart(3, '0')}` : '',
    temperature: currentNode >= 4 ? parseFloat((2 + Math.random() * 6).toFixed(1)) : 0,
    humidity: currentNode >= 4 ? parseFloat((50 + Math.random() * 30).toFixed(1)) : 0,
    location: currentNode >= 6 ? ports[4 + (index % 4)] : ports[index % 4],
    status: currentNode >= 9 ? ContainerStatus.AT_DESTINATION : currentNode >= 6 ? ContainerStatus.IN_TRANSIT : ContainerStatus.AT_ORIGIN,
    vesselName: vessel.name,
    voyageNo: vessel.voyage,
    estimatedArrival: new Date(Date.now() + (index + 1) * 86400000 * 3).toISOString(),
    actualArrival: currentNode >= 9 ? new Date(Date.now() - 86400000).toISOString() : '',
    archiveNo,
    category: order.category,
    route: order.route,
    remainingStay: computeRemainingStay(currentNode, index),
    // —— 双线 / 子步骤 关联字段（§7.3 推导规则）——
    // 进入装柜(N4)及之后才有订舱号(S/O)；N4 节点内部子步骤停在 loading。
    bookingNo: currentNode >= 4 ? `SO-${order.orderNo}` : '',
    currentSubstep: currentNode === 4 ? 'loading' : undefined,
  };
}

/** 从订单派生柜档案列表（含演示柜 CONT_TEST，用于「4.1 完成后弹新建柜」） */
export const mockContainers: Container[] = [
  ...mockOrders.map((order, i) => deriveContainerFromOrder(order, i)),
  testAtPickup,
];
export const mockContainerAlertSummary: ContainerAlertSummary = deriveAlertSummary(mockContainers);

/**
 * 按 §7.3 规则为单个柜生成子步骤：
 *   produce → goods_prep + booking；load → loading + seal_iot + transport（初始全 pending）。
 * 推导：
 *   - currentNode >= 4 → 两线置 completed，load 子步骤待处理，currentSubstep='loading'
 *   - currentNode > 4  → loading / seal_iot 追加 completed
 *
 * ⚠️ 2026-08-08 装柜子步骤灵活配置：pickup_empty 已退役；建柜档案入口改为
 * node_substeps 中 execution_mode='ACTION_ENTRY' & action_type='ARCHIVE_CREATE' 的行，
 * 由 shouldShowNewArchive / node-action-hint 驱动，不在此 produce+load 的序列内。
 */
function buildContainerSubsteps(container: Container): ContainerSubstep[] {
  const now = new Date().toISOString();
  const produceDone = container.currentNode >= 4;
  const loadDone = container.currentNode > 4;
  const sealDone = container.currentNode > 4;

  const goodsChecklist: SubstepChecklistItem[] = GOODS_PREP_CHECKLIST.map((label, i) => ({
    id: `chk-${i}`,
    label,
    done: produceDone,
    doneAt: produceDone ? now : undefined,
  }));

  return [
    {
      id: `${container.id}-produce-goods_prep`,
      containerId: container.id,
      nodeCode: 'produce',
      substepKey: 'goods_prep',
      line: 'goods',
      status: produceDone ? 'completed' : 'pending',
      completedAt: produceDone ? now : undefined,
      checklist: goodsChecklist,
    },
    {
      id: `${container.id}-produce-booking`,
      containerId: container.id,
      nodeCode: 'produce',
      substepKey: 'booking',
      line: 'logistics',
      status: produceDone ? 'completed' : 'pending',
      completedAt: produceDone ? now : undefined,
    },
    {
      id: `${container.id}-load-loading`,
      containerId: container.id,
      nodeCode: 'load',
      substepKey: 'loading',
      status: loadDone ? 'completed' : 'pending',
      completedAt: loadDone ? now : undefined,
    },
    {
      id: `${container.id}-load-seal_iot`,
      containerId: container.id,
      nodeCode: 'load',
      substepKey: 'seal_iot',
      status: sealDone ? 'completed' : 'pending',
      completedAt: sealDone ? now : undefined,
    },
    {
      id: `${container.id}-load-transport`,
      containerId: container.id,
      nodeCode: 'load',
      substepKey: 'transport',
      status: 'pending',
      completedAt: undefined,
    },
  ];
}

/** 所有柜的子步骤集合（柜档案粒度：每柜 produce 2 行 + load 3 行） */
export const mockContainerSubsteps: ContainerSubstep[] = mockContainers.flatMap(buildContainerSubsteps);
